import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  Gauge,
  Heart,
  MoonStars,
  Package,
  SignOut,
  WarningCircle,
} from "@phosphor-icons/react";

import { MarketShell } from "../../design-system/components/shells";
import { MY_ORDERS_TO } from "../../design-system/components/shells/navModel";
import { Empty, LoadingBlock, PageTitle } from "../../design-system/components/desktop";
import { MenuList, MenuRow, Switch } from "../../design-system/components/mobile";
import { useViewport } from "../../design-system/hooks/useViewport";
import { useTheme } from "../../Contexts/ThemeContext";
import {
  Panel,
  PanelAction,
  PanelStack,
} from "../../design-system/patterns/settings";
import { ConfirmForm } from "../../design-system/patterns/modal-form";
import { routes } from "../../routes/routes";

import AddressSection from "./components/AddressSection";
import BankDetailsForm from "./components/BankDetailsForm";
import BasicInfo from "./components/BasicInfo";
import BuyerEditProfileForm from "./components/BuyerEditProfileForm";
import DeliverySection from "./components/DeliverySection";
import EditDeliveryForm from "./components/EditDeliveryForm";
import EditProfileForm from "./components/EditProfileForm";
import EditStoreInfoForm from "./components/EditStoreInfoForm";
import PaymentMethodsList from "./components/PaymentMethodsList";
import PayoutRequestModal from "./components/PayoutRequestModal";
import RecentOrdersSection from "./components/RecentOrdersSection";
import SavedAddresses from "./components/SavedAddresses";
import SavedCardsSection from "./components/SavedCardsSection";
import StoreDetailsSection from "./components/StoreDetailsSection";
import AddressFormModal from "./components/AddressFormModal";
import WalletSection from "./components/WalletSection";

import { errorText, LOAD_FAILURE } from "../../utils/errorText";

import "./Profile.css";

/**
 * /profile, the view. Pure: every input is a prop, every output is a callback.
 *
 * The container (`Profile.jsx`) owns the eight queries, the six mutations, the
 * role gate, the toasts and the Redux write-back. This file owns the shape,
 * which is why the state explorer can mount the real screen with fixtures and
 * see a buyer profile, a seller profile, an empty one and a failed one without
 * the API running.
 *
 * THE SHAPE, AND WHY IT IS NOT THE LIBRARY TEMPLATE
 * ------------------------------------------------
 * `settings.html` upstream is a two-column page with a left settings nav and a
 * save bar. This page is neither. It is a single stacked column of independent
 * sections, each committing through its own dialog, so there is nothing for a
 * nav to navigate between and nothing for a save bar to save. `CONVERSION-MAP`
 * B7 says exactly this. The promoted `.dsk-panel` is what was taken from the
 * template; its chrome was not.
 *
 * TWO ROLES, ONE PAGE
 * -------------------
 * A buyer sees four sections, a seller sees six, and they are not the same four
 * plus two. The buyer's address is read-only and edited through the profile
 * dialog; the seller's is a working collection address with its own form. That
 * fork is data, not layout, so it lives here as two branches over one stack
 * rather than as two pages.
 */

export function ProfileView({
  isBuyer = false,
  loading = false,
  error,

  profile,
  wallet,
  sellerInfo,
  sellerInfoLoading = false,
  sellerInfoError,
  deliveryAddress,

  addresses = [],
  addressesLoading = false,

  bankDetails,
  bankDetailsLoading = false,
  bankDetailsError,

  orders = [],
  ordersLoading = false,
  savedCards = [],
  savedCardsLoading = false,

  storeUnavailable = false,
  availabilityLoading = false,
  onToggleAvailability,

  saving = false,
  deleting = null,

  editProfileOpen = false,
  onOpenEditProfile,
  onCloseEditProfile,
  onSubmitProfile,

  editDeliveryOpen = false,
  onOpenEditDelivery,
  onCloseEditDelivery,
  onSubmitDelivery,

  editStoreOpen = false,
  onOpenEditStore,
  onCloseEditStore,
  onSubmitStore,

  bankFormOpen = false,
  editingBankDetails = null,
  onOpenAddBank,
  onOpenEditBank,
  onCloseBankForm,
  onSubmitBank,

  deleteBankOpen = false,
  onRequestDeleteBank,
  onConfirmDeleteBank,
  onCancelDeleteBank,

  addressFormOpen = false,
  editingAddress = null,
  addressSubmitting = false,
  onOpenAddAddress,
  onOpenEditAddress,
  onCloseAddressForm,
  onSubmitAddress,

  deleteAddressOpen = false,
  addressToDelete = null,
  addressDeleting = false,
  onRequestDeleteAddress,
  onConfirmDeleteAddress,
  onCancelDeleteAddress,

  payoutOpen = false,
  onOpenPayout,
  onClosePayout,
  onPayoutSuccess,
  onSignOut,
}) {
  const hasBankDetails = Array.isArray(bankDetails) && bankDetails.length > 0;
  const { isDesktop } = useViewport();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <MarketShell
      title="Account"
      /* These two crumbs are also what puts the BACK CONTROL on this page —
         the floating button on mobile, the link before the trail on desktop.
         `MarketShell` derives it from the last crumb carrying a `to`, so the
         linked "Marketplace" crumb is load-bearing: drop the `to` and the
         account screen goes back to having no way out but the tab bar, which
         is the state Junaid reported. Account is a tab and still gets one,
         because unlike Marketplace / Games / Dashboard nothing lives under
         /profile and it is entered from a menu row, the user menu or a deep
         link as often as from the bar. See SECTION_ROOTS in MarketShell. */
      breadcrumbs={[
        { label: "Marketplace", to: routes.MARKETPLACE },
        { label: "Profile" },
      ]}
      /* `account`, not `profile`. The tab's id in MARKET_TABS is `account`, and
         `TabBar` compares `activeId` against the item id exactly, so
         `activeNav="profile"` matched nothing and the Account tab never lit up
         on its own page. */
      activeNav="account"
    >
      {/* Each track owns its own type scale. `PageTitle` is `.dsk-page-title`
          at 28px with no media query, where the mobile screen title is 24px —
          the same branch tiles.jsx:43 makes for the same reason. */}
      {isDesktop ? (
        <PageTitle>Account</PageTitle>
      ) : (
        <h1 className="ds-screen-title">Account</h1>
      )}

      {/* THE ACCOUNT SECTION, mobile only.
          Three things moved in here off the primary chrome, and each for the
          same reason: the bottom bar and the 44px header slot are for what you
          reach for constantly, and none of these is that.

            Orders    was a bottom-bar tab pointing at `/order-tracking`, a
                      track-by-reference lookup rather than the buyer's orders.
                      It now goes where their orders actually are.
            Dashboard was reachable only from the desktop user menu, so a
                      seller on a phone had no way into their own store.
            Theme     was a permanent icon in the header. It is a preference you
                      set once, so it becomes a row with a switch and the header
                      slot goes to the cart.

          Desktop already has all three in `UserMenu`, so this does not render
          there and neither track ends up with two copies. */}
      {!isDesktop && (
        <MenuList aria-label="Account">
          {/* COLLECTION. No longer the ONLY way into `/collection` on a phone —
              the tab bar carries it now (`navModel.js`), because a row two taps
              deep is not an answer to "I hearted this card, where did it go"
              asked from the page you hearted it on.

              It stays here anyway, and deliberately: desktop lists Collection
              in BOTH `TopNav` and `UserMenu`, so an account menu that omitted
              it would be the one place in the app where the two tracks
              disagree about what an account contains.

              First in the list, which is where the desktop user menu puts it
              too: Profile, Collection, Seller dashboard, Orders. Same order on
              both tracks, minus the Profile row, because on mobile this list IS
              the profile page. */}
          <MenuRow
            icon={<Heart />}
            label="Collection"
            onClick={() => navigate(routes.COLLECTION)}
          />
          <MenuRow
            icon={<Package />}
            label="Orders"
            onClick={() => navigate(MY_ORDERS_TO)}
          />
          {/* SELLERS ONLY, and it was not gated. `routes.DASHBOARD` resolves by
              role, so a buyer tapping a row labelled "Seller dashboard" landed
              on the BUYER dashboard — a label naming a thing its destination is
              not. The row also duplicated a tab: `MARKET_TABS` carries Dashboard
              as `authOnly` (navModel.js:135), so a signed-in buyer had two rows
              pointing at one route under two names. The comment above justifies
              this row on the grounds that a seller on a phone had no way into
              their own store, which is still true for a seller and no longer
              true for a buyer, so the gate rather than the delete. */}
          {!isBuyer && (
            <MenuRow
              icon={<Gauge />}
              label="Seller dashboard"
              onClick={() => navigate(routes.DASHBOARD)}
            />
          )}
          <MenuRow
            icon={<MoonStars />}
            label="Dark mode"
            chevron={false}
            /* The switch IS the control, so the row must not also be a
               button that toggles it: a tap on the switch would fire both
               handlers and cancel itself. The row carries the switch and
               nothing else. */
            onClick={undefined}
            trailing={
              <Switch
                checked={Boolean(isDarkMode)}
                /* `onCheckedChange`, NOT `onChange`, and the distinction is
                   per TRACK. The mobile `Switch` — this one — is a <button>
                   with `role="switch"`, so an `onChange` prop lands on the
                   button through the rest spread and never fires: that is why
                   dark mode did nothing. The DESKTOP `Switch` is a real
                   `<input type="checkbox">` and takes `onChange` correctly,
                   which is why the store-availability toggles still do. */
                onCheckedChange={toggleTheme}
                aria-label="Dark mode"
              />
            }
          />
          <MenuRow
            icon={<SignOut />}
            label="Sign out"
            danger
            chevron={false}
            onClick={onSignOut}
          />
        </MenuList>
      )}

      {loading ? (
        <LoadingBlock>Loading your profile</LoadingBlock>
      ) : (
        <PanelStack>
          {error ? (
            <Empty
              icon={WarningCircle}
              illustration="request-failed"
              tone="error"
              title="We could not load your profile"
              text={errorText(error, LOAD_FAILURE)}
            />
          ) : null}

          <BasicInfo profile={profile} onEdit={onOpenEditProfile} />

          <SavedAddresses
            addresses={addresses}
            isLoading={addressesLoading}
            onAdd={onOpenAddAddress}
            onEdit={onOpenEditAddress}
            onDelete={onRequestDeleteAddress}
          />

          {isBuyer ? (
            <>
              {deliveryAddress ? (
                <AddressSection address={deliveryAddress} onEdit={onOpenEditProfile} />
              ) : null}

              <Panel
                icon={Package}
                title="Recent orders"
                action={
                  <PanelAction to={`${routes.DASHBOARD}?tab=order_history`}>
                    See all orders
                  </PanelAction>
                }
              >
                <RecentOrdersSection orders={orders} isLoading={ordersLoading} />
              </Panel>

              <Panel
                icon={CreditCard}
                title="Saved cards"
                action={
                  <PanelAction to={routes.DASHBOARD}>
                    See all saved cards
                  </PanelAction>
                }
              >
                <SavedCardsSection cards={savedCards} isLoading={savedCardsLoading} />
              </Panel>
            </>
          ) : (
            <>
              <WalletSection
                wallet={wallet}
                hasBankDetails={hasBankDetails}
                onRequestPayout={onOpenPayout}
              />

              <DeliverySection address={deliveryAddress} onEdit={onOpenEditDelivery} />

              <Panel
                icon={CreditCard}
                title="Payment methods"
                sub="Where your payouts are sent."
                action={<PanelAction onClick={onOpenAddBank}>Add payment method</PanelAction>}
              >
                <PaymentMethodsList
                  bankDetails={bankDetails}
                  isLoading={bankDetailsLoading}
                  error={bankDetailsError}
                  onEdit={onOpenEditBank}
                  onDelete={onRequestDeleteBank}
                  deleting={deleting}
                />
              </Panel>

              <StoreDetailsSection
                sellerInfo={sellerInfo}
                isLoading={sellerInfoLoading}
                error={sellerInfoError}
                storeUnavailable={storeUnavailable}
                availabilityLoading={availabilityLoading}
                onToggleAvailability={onToggleAvailability}
                onEdit={onOpenEditStore}
              />
            </>
          )}
        </PanelStack>
      )}

      {/* ------------------------------------------------------------ modals */}

      {editProfileOpen &&
        (isBuyer ? (
          <BuyerEditProfileForm
            open
            initial={profile}
            submitting={saving}
            onCancel={onCloseEditProfile}
            onSubmit={onSubmitProfile}
          />
        ) : (
          <EditProfileForm
            open
            initial={profile}
            submitting={saving}
            onCancel={onCloseEditProfile}
            onSubmit={onSubmitProfile}
          />
        ))}

      {editDeliveryOpen && (
        <EditDeliveryForm
          open
          initial={deliveryAddress}
          submitting={saving}
          onCancel={onCloseEditDelivery}
          onSubmit={onSubmitDelivery}
        />
      )}

      {editStoreOpen && (
        <EditStoreInfoForm
          open
          initial={sellerInfo}
          submitting={saving}
          onCancel={onCloseEditStore}
          onSubmit={onSubmitStore}
        />
      )}

      {bankFormOpen && (
        <BankDetailsForm
          open
          initial={editingBankDetails}
          submitting={saving}
          onCancel={onCloseBankForm}
          onSubmit={onSubmitBank}
        />
      )}

      {addressFormOpen && (
        <AddressFormModal
          open
          initial={editingAddress}
          submitting={addressSubmitting}
          onCancel={onCloseAddressForm}
          onSubmit={onSubmitAddress}
        />
      )}

      <PayoutRequestModal
        isOpen={payoutOpen}
        currentBalance={wallet?.balance ?? 0}
        onClose={onClosePayout}
        onSuccess={onPayoutSuccess}
      />

      <ConfirmForm
        open={deleteBankOpen}
        title="Delete payment method"
        message="This payment method will be removed from your account and payouts will no longer be sent to it."
        detail="You can add it again at any time."
        confirmLabel="Delete payment method"
        confirmingLabel="Deleting"
        loading={Boolean(deleting)}
        onConfirm={onConfirmDeleteBank}
        onClose={onCancelDeleteBank}
      />

      <ConfirmForm
        open={deleteAddressOpen}
        title="Delete address"
        message="This address will be removed from your saved addresses."
        detail={
          addressToDelete
            ? [
                addressToDelete.address_line1,
                addressToDelete.address_line2,
                addressToDelete.city,
                addressToDelete.province,
                addressToDelete.postal_code,
              ]
                .filter(Boolean)
                .join(", ")
            : undefined
        }
        confirmLabel="Delete address"
        confirmingLabel="Deleting"
        loading={addressDeleting}
        onConfirm={onConfirmDeleteAddress}
        onClose={onCancelDeleteAddress}
      />
    </MarketShell>
  );
}

export default ProfileView;
